import { CustomEventType } from "@/events/CustomEventRegistry";
import { IEventDataSource } from "@/types/interfaces";
import { BigBashEvent, Game, RoomDisplayNames, RoomName, RoundPointsFieldEnum, ScreenContent, ScreenContentEnum } from "@/types/types";
import { startReportQueueRetries, stopReportQueueRetries } from "@infrastructure/reportQueue";
import { FeatureFlags } from "@lib/features";
import { logError } from "@lib/logger";
import { backBtnStyle, sharedStyles } from "@lib/sharedStyles";
import { LitElement, TemplateResult, css, html } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import { reportDelayProblem } from "./delayProblemReportHandler";
import { GamePointsPoller } from "./GamePointsPoller";
import { ModeratorWebsocketController } from "./moderatorWebsocketController";
import { saveRoundPointValue } from "./roundPointSaveHandler";
import { saveRoundTargetValue } from "./roundTargetValueSaveHandler";
import { QueueAndRunRequests } from "@infrastructure/queueAndRunRequests";
import { UpdateRoundPointValue } from "./types/updateRoundPointValue";
import { UpdateTargetValue } from "./types/updateTargetValue";

import './JeopardyModeratorPanel';
import './MillionaireModeratorPanel';

const DELAY_REPORT_CONFIRMATION_DURATION_MS = 3_000;

@customElement("moderate-game-page")
export class ModerateGamePage extends LitElement {
	static readonly styles = [
		sharedStyles,
		backBtnStyle,
		css`
			.container {
				max-width: 1200px;
				min-width: 1000px;
				border: var(--border-default);
				border-radius: var(--border-radius);
				background: white;
				box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1);
				padding: 16px;
				display: flex;
				flex-direction: column;
				gap: 16px;
			}

			.delay-report-button {
				position: fixed;
				bottom: 8px;
				left: 8px;
			}

			.container-middle {
				display: flex;
				justify-content: space-between;
				align-items: center;
			}

			.container-bottom {
				display: flex;
				flex-direction: column;
				align-items: center;
				gap: 16px;
				background: var(--color-pink);
				padding: 16px;
			}

			.container-bottom__button-container {
				display: flex;
				gap: 8px;
			}

			round-value-calculator, jeopardy-moderator-panel {
				width: 100%;
			}
		`
	];

	@state() private event: BigBashEvent;
	@state() private game: Game;
	@state() private isLoading = true;
	@state() private isDelayReportSent = false;
	@state() private isPollInFlight = false;
	@state() private isSaveInFlight = false;
	@state() private pollConsecutiveFailureCount = 0;
	@property({}) eventId: string;
	@property({}) gameId: string;
	@property({ type: Object }) eventDataSource: IEventDataSource;
	private wsController: ModeratorWebsocketController | null = null;
	@state() private selectedQuizId: string | null = null;
	private gamePointsPoller: GamePointsPoller | null = null;
	private queuedRequestResolvers: Array<() => void> = [];

	constructor(eventDataSource: IEventDataSource, eventId: string, gameId: string) {
		super();
		this.eventDataSource = eventDataSource;
		this.eventId = eventId;
		this.gameId = gameId;
		this.addEventListener(CustomEventType.EVENT_UPDATED, this._handleJeopardyEventUpdated as EventListener);
		this.addEventListener('millionaire-broadcast', this._handleMillionaireBroadcast as EventListener);
	}

	async connectedCallback(): Promise<void> {
		super.connectedCallback();
		// read quizId from query params (if present)
		try {
			const params = new URLSearchParams(window.location.search);
			const qid = params.get('quizId');
			if (qid) this.selectedQuizId = qid;
		} catch (e) {
			// ignore
		}
		if (FeatureFlags.delayProblemReportButton) {
			startReportQueueRetries();
		}
		await this._tryToLoadEventData();
	}

	disconnectedCallback(): void {
		super.disconnectedCallback();
		if (FeatureFlags.delayProblemReportButton) {
			stopReportQueueRetries();
		}
		this.gamePointsPoller?.stop();
		this.gamePointsPoller = null;
		this.wsController?.close();
		this.wsController = null;
	}

	private async _tryToLoadEventData() {
		this.isLoading = true;
		try {
			const event = await this.eventDataSource.getEventById(this.eventId);
			this._handleEventResult(event);
		} catch (error) {
			this._handleFetchError(error);
		} finally {
			this.isLoading = false;
		}
	}

	private _handleFetchError(error: unknown): void {
		logError("Error fetching event:", error);
		alert("Failed to load event. Please try again later.");
	}

	private _handleEventResult(event: BigBashEvent): void {
		this.event = event;
		const game = event.games.find((game) => game.id === this.gameId);
		if (!game) {
			logError('Game not found in event', { eventId: this.eventId, gameId: this.gameId });
			alert('Spiel konnte nicht gefunden werden. Bitte versuche es erneut.');
			return;
		}
		this.game = game;
		this.wsController = new ModeratorWebsocketController(game.default_room);
		this.gamePointsPoller = new GamePointsPoller(
			() => { this.isPollInFlight = true; },
			(updatedEvent, consecutiveFailureCount) => {
				this.event = updatedEvent;
				this.pollConsecutiveFailureCount = consecutiveFailureCount;
				this.isPollInFlight = false;
			},
			game,
			event,
			this.eventDataSource
		);
		this.gamePointsPoller.start();
	}

	private readonly queueAndRunRequests: QueueAndRunRequests = async <TResult,>(operation: () => Promise<TResult>): Promise<TResult> => {
		this.gamePointsPoller?.invalidateInFlightPollRequestAndResetSchedule();
		if (this.isSaveInFlight) {
			await new Promise<void>(resolve => this.queuedRequestResolvers.push(resolve));
		}
		this.isSaveInFlight = true;
		try {
			return await operation();
		} finally {
			this.isSaveInFlight = false;
			this.queuedRequestResolvers.shift()?.();
		}
	};

	private readonly updateRoundPointValue: UpdateRoundPointValue = async (
		gameId: string,
		teamId: string,
		roundNumber: number,
		field: RoundPointsFieldEnum,
		value: number
	): Promise<BigBashEvent> => {
		await this.queueAndRunRequests(async () => {
			const updatedEvent = await saveRoundPointValue({
				eventDataSource: this.eventDataSource,
				event: this.event,
				gameId,
				teamId,
				roundNumber,
				field,
				value,
			});
			this._setEvent(updatedEvent);
		});
		return this.event;
	};

	private readonly updateTargetValue: UpdateTargetValue = async (
		gameId: string,
		roundNumber: number,
		value: number
	): Promise<BigBashEvent> => {
		await this.queueAndRunRequests(async () => {
			const updatedEvent = await saveRoundTargetValue({
				eventDataSource: this.eventDataSource,
				event: this.event,
				gameId,
				roundNumber,
				value,
			});
			this._setEvent(updatedEvent);
		});
		return this.event;
	};

	private _sendToRoom(screenContent: ScreenContent): void {
		const message = JSON.stringify({ screenContent, event: this.event, game: this.game });
		this.wsController?.send(message);
	}

	private readonly _handleButtonClick = (screenContent: ScreenContent) => {
		this._sendToRoom(screenContent);
	};

	private readonly _handleReportDelayProblem = (): void => {
		void reportDelayProblem(this.event, this.game, this.wsController?.getDiagnosticsSnapshot() ?? null);
		this.isDelayReportSent = true;
		setTimeout((): void => {
			this.isDelayReportSent = false;
		}, DELAY_REPORT_CONFIRMATION_DURATION_MS);
	};

	private readonly _handleBackClick = (event: MouseEvent): void => {
		event.preventDefault();
		window.history.pushState({}, '', `/host/moderate-event/${this.eventId}`);
		window.dispatchEvent(new PopStateEvent('popstate'));
	};

	private _setEvent(event: BigBashEvent): void {
		this.event = event;
		this.gamePointsPoller?.setEvent(event);
	}

	private readonly _onGamePointsUpdated = (event: BigBashEvent) => {
		this._setEvent(event);
	};

	private _renderCalculator(): TemplateResult {
		return html`
			<round-value-calculator
				.eventDataSource="${this.eventDataSource}"
				.game="${this.game}"
				.event="${this.event}"
				.pointCalculationType="${this.game.point_calculation}"
				.onGamePointsUpdated="${this._onGamePointsUpdated}"
				.queueAndRunRequests="${this.queueAndRunRequests}"
				.updateRoundPointValue="${this.updateRoundPointValue}"
				.updateTargetValue="${this.updateTargetValue}"
				.isPollInFlight="${this.isPollInFlight}"
				.consecutiveFailureCount="${this.pollConsecutiveFailureCount}"
			></round-value-calculator>
		`;
	}

	private _handleJeopardyEventUpdated = (ev: CustomEvent) => {
		const updatedEvent = ev.detail?.event;
		if (!updatedEvent) { logError('event-updated received without event payload'); return; }
		this._setEvent(updatedEvent);
		this.game = updatedEvent.games.find((g: Game) => g.id === this.gameId) ?? this.game;
		this.requestUpdate();
	};

	private _handleResetGamePoints = async () => {
		const hasPoints = this.event?.game_points?.some(gp => gp.game === this.game.id);
		if (!hasPoints) {
			alert('Dieses Spiel hat noch keine Punkte.');
			return;
		}

		const confirmed = window.confirm('Punkte für dieses Spiel wirklich zurücksetzen?');
		if (!confirmed) return;

		try {
			await this.queueAndRunRequests(() => this.eventDataSource.deleteGamePointsForGame(this.eventId, this.gameId));
			const freshEvent = await this.queueAndRunRequests(() => this.eventDataSource.getEventById(this.eventId));
			this._setEvent(freshEvent);
			this.game = this.event.games.find(g => g.id === this.gameId)!;
			this.requestUpdate();
			this._sendToRoom(ScreenContentEnum.totalPoints);
		} catch (err) {
			logError('Failed to reset game points', err);
			alert('Fehler beim Zurücksetzen der Punkte.');
		}
	};

	private _handleMillionaireBroadcast = (ev: CustomEvent) => {
		const detail = ev.detail || {};

		this._sendCustomPayload(ScreenContentEnum.millionaire, {
			question: detail.question,
			showAnswer: detail.showAnswer,
			selectedOptionId: detail.selectedOptionId
		});
	};

	private _sendCustomPayload(screenContent: ScreenContent, extras: any) {
		const message = JSON.stringify({ screenContent, event: this.event, game: this.game, ...extras });
		this.wsController?.send(message);
	}

	render() {

		if (this.isLoading) {
			return html`
				<a href="/host/moderate-event/${this.eventId}" class="back-btn" @click="${this._handleBackClick}">←</a>
				<div class="container">
					<div style="text-align: center; padding: 60px;">
						Loading events...
					</div>
				</div>
			`;
		}

		return html`
			<a href="/host/moderate-event/${this.eventId}" class="back-btn" @click="${this._handleBackClick}">←</a>
			<div class="container">
				<div class="container-top">
					<h1 class="text-headline">${this.game.name}</h1>
					<p class="text-body">${this.game.description}</p>
					<p class="text-body">${this.game.rules}</p>
				</div>

				<div class="container-middle">
				</div>

				<div class="container-bottom">
					<h2>Steuere den Bildschirm im Raum ${RoomDisplayNames[this.game.default_room as RoomName]}</h2>
					<div class="container-bottom__button-container">
						<basic-button @click="${() => this._handleButtonClick(ScreenContentEnum.default)}">Bildschirmschoner</basic-button>
						<basic-button @click="${() => this._handleButtonClick(ScreenContentEnum.gamePoints)}">Ergebnis dieses Spiels</basic-button>
						<basic-button @click="${() => this._handleButtonClick(ScreenContentEnum.totalPoints)}">Aktueller Punktestand</basic-button>
						<alert-button @click="${this._handleResetGamePoints}">Punkte zurücksetzen</alert-button>
					</div>
				</div>
				<div class="container-bottom">
					${this._renderCalculator()}
				</div>

			<!-- Jeopardy moderator panel: appears when the game is Jeopardy or a quizId is present -->
			${(this.game?.name && this.game.name.toLowerCase().includes('jeopardy')) || this.selectedQuizId ? html`
				<jeopardy-moderator-panel
					.event=${this.event}
					.game=${this.game}
					.eventDataSource=${this.eventDataSource}
					.queueAndRunRequests=${this.queueAndRunRequests}
					.updateRoundPointValue=${this.updateRoundPointValue}
					quizId="${this.selectedQuizId ?? ''}"
				></jeopardy-moderator-panel>
			` : ''}

			${(this.game?.name && this.game.name.toLowerCase().includes('millionaire')) ? html`
				<millionaire-moderator-panel .event=${this.event} .game=${this.game}></millionaire-moderator-panel>
			` : ''}

			${FeatureFlags.delayProblemReportButton ? html`
				<warning-button small class="delay-report-button" @click="${this._handleReportDelayProblem}">
					${this.isDelayReportSent ? 'Gesendet' : 'Delay Problem melden'}
				</warning-button>
			` : ''}

		</div>
	`;
	}
}

export default ModerateGamePage;
